import { ButtonVariant } from '@vimily/bonjoro-ui-components';
import { Button } from '@vimily/bonjoro-ui-components-v2/Button';
import { Modal } from '@vimily/bonjoro-ui-components-v2/Modal';
import React, { useRef, useState, useMemo, useEffect } from 'react';
import { useUpdatePublishable } from '~/App/Api/Publishable/api';
import useStateObject from '~/react-hooks/use-state-object';
import { PublishableTestimonial } from '~/types/models/publishable';
import { usePublishPageContext } from '../Contexts/PublishPageContext';
import { DisplayOrder } from '../Containers/Sidebar/Components/Sections/Select';
import { useOrderedTestimonials } from '../hooks';
import DraggableTile from './OrderTestimonialsModal/DraggableTile';
import { usePublishEditorState } from '../Contexts/PublishEditorContext';

export interface OrderTestimonialsModalProps {
  open?: boolean;
  onModalClose?: () => void;
}

export default function OrderTestimonialsModal({
  open,
  onModalClose: closeModal,
}: OrderTestimonialsModalProps) {
  const dropRef = useRef<HTMLDivElement>(null);

  const { publish } = usePublishPageContext();

  const oldOrderedTestimonials = useOrderedTestimonials(publish, {
    displayOrder: DisplayOrder.Manual,
  });
  const oldOrder = useMemo(
    () => oldOrderedTestimonials.map((pub) => pub.uuid),
    [publish?.publishable_resources, oldOrderedTestimonials]
  );

  const [{ displayOrder }] = usePublishEditorState();

  const [{ order }, { isDirty, set, done }] = useStateObject({
    order: oldOrder,
  });

  // Update order whenever publishable resources have been updated
  useEffect(() => {
    set('order', oldOrder);
  }, [publish?.publishable_resources]);

  const newOrderedTestimonials = useOrderedTestimonials(publish, {
    order,
    displayOrder: DisplayOrder.Manual,
  });

  const [dragSource, setDragSource] = useState<PublishableTestimonial>();
  const [dragTarget, setDragTarget] = useState<PublishableTestimonial>();

  function handleDragEnd() {
    if (
      order &&
      dragSource &&
      dragTarget &&
      dragSource.uuid !== dragTarget.uuid
    ) {
      const sourceIndex = order.indexOf(dragSource.uuid);
      const targetIndex = order.indexOf(dragTarget.uuid);

      if (sourceIndex >= 0 && targetIndex >= 0) {
        // remove sourceIndex from the array
        const tempArr = [...order];
        tempArr.splice(sourceIndex, 1);

        // get new index of target
        const tempTargetIndex = tempArr.indexOf(dragTarget.uuid);

        let newArr: string[];

        // if moving left: source goes in front of target
        // if moving right: source goes behind target
        if (sourceIndex > targetIndex) {
          newArr = [
            ...tempArr.slice(0, tempTargetIndex),
            dragSource.uuid,
            ...tempArr.slice(tempTargetIndex),
          ];
        } else {
          newArr = [
            ...tempArr.slice(0, tempTargetIndex),
            dragTarget.uuid,
            dragSource.uuid,
            ...tempArr.slice(tempTargetIndex + 1),
          ];
        }

        set('order', newArr);
      }
    }

    setDragSource(undefined);
    setDragTarget(undefined);
  }

  const { mutateAsync: updatePublishable, isLoading } = useUpdatePublishable();

  async function handleSaveClick() {
    if (isDirty) {
      await updatePublishable(
        {
          uuid: publish.uuid,
          metadata: {
            ...publish.metadata,
            display_order: displayOrder,
            order,
          },
        },
        {
          onSuccess: done,
        }
      );
    }

    closeModal?.();
  }

  return (
    <Modal
      open={open}
      onModalClose={closeModal}
      className='max-w-[1160px] rounded-lg h-[800px] !p-0'
      panelClassName='h-full mb-6'
      hideCloseButton
    >
      <div className='absolute top-6 right-6'>
        <Button
          variant={ButtonVariant.Secondary}
          className='mr-5'
          onClick={closeModal}
          disabled={isLoading}
        >
          Cancel
        </Button>
        <Button
          variant={ButtonVariant.Primary}
          onClick={handleSaveClick}
          disabled={isLoading || !isDirty}
        >
          Save
        </Button>
      </div>

      <div className='mt-2 mb-5 px-7 pt-7'>
        <h1 className='text-3xl p-0 m-0'>Order testimonials</h1>
        <p className='text-base mt-5'>Drag testimonials to change order</p>
      </div>

      <div
        className='v2-bg-neutral-50 h-full p-7 rounded-b-lg grid grid-cols-3 gap-6 max-h-[750px] overflow-auto'
        ref={dropRef}
        onDragOver={(e) => {
          e.preventDefault();
        }}
      >
        {newOrderedTestimonials.map((publishable, index) => {
          return (
            <DraggableTile
              key={publishable.uuid}
              orderNumber={index + 1}
              containerClassName='h-full min-h-[350px] w-full'
              dragContainerClassName='h-auto min-h-[350px]'
              className='h-full overflow-hidden'
              detail={publishable}
              onDragEnter={() => setDragTarget(publishable)}
              onDragExit={() =>
                setDragTarget((old) => {
                  if (old && old.uuid === publishable.uuid) {
                    return undefined;
                  }

                  return old;
                })
              }
              onDragStart={() => setDragSource(publishable)}
              onDragEnd={handleDragEnd}
            />
          );
        })}
      </div>
    </Modal>
  );
}
